import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Handshake, MessageSquare, Pause, PhoneOff, Play, User, UserX, Voicemail } from "lucide-react";
import { usePowerDialer } from "@/context/PowerDialerContext";
import {
  useGetCallDispositionsQuery,
  useGetContactQuery,
  useGetDealsQuery,
  useGetVoicemailDropStatusQuery,
  useMarkLeadDoNotCallMutation,
  useMarkLeadWrongNumberMutation,
} from "@/store/rest_api";
import { useAuth } from "@/hooks/useAuth";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { dispositionShortcuts } from "@/util/dispositions";
import { CallScriptPane } from "@/components/dialer/power-dialer/CallScriptPane";
import { LeadPropertyCards } from "@/components/dialer/power-dialer/LeadPropertyCards";
import { LeadHistorySheet } from "@/components/dialer/power-dialer/LeadHistorySheet";
import { LineSlotStrip } from "@/components/dialer/power-dialer/LineSlotStrip";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiErrorMessage } from "@/util/apiError";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import CallControls from "@/components/dialer/components/CallControls";
import RecordingPill from "@/components/dialer/components/RecordingPill";
import { DispositionDock } from "@/components/dialer/components/DispositionDock";
import { formatTime } from "@/util/date";
import { connectSeconds, phoneSkipLabel } from "@/util/dialer";
import { getContactDisplayName, getInitials } from "@/util/string";
import { NotesSaveState } from "@/enum/NotesSaveState";
import { cn } from "@/lib/utils";

interface PowerDialerCallPaneProps {
  onKeypadPressed: () => void;
  areShortcutsPaused?: boolean;
}

export function PowerDialerCallPane({ onKeypadPressed, areShortcutsPaused = false }: PowerDialerCallPaneProps) {
  const navigate = useNavigate();
  const {
    activeCall,
    roomCall,
    endCall,
    holdCall,
    resumeCall,
    dropVoicemail,
    isCallActionPending,
    setNotes,
    saveAndNext,
    selectDisposition,
    notesSaveStates,
    campaign,
    settings,
  } = usePowerDialer();
  const [markDoNotCall, { isLoading: isMarkingDoNotCall }] = useMarkLeadDoNotCallMutation();
  const [markWrongNumber, { isLoading: isMarkingWrongNumber }] = useMarkLeadWrongNumberMutation();
  const { data: contactDeals } = useGetDealsQuery(
    { contactId: activeCall?.contact.id || undefined, size: 3 },
    { skip: !activeCall?.contact.id },
  );
  const { data: fullContact } = useGetContactQuery(activeCall?.contact.id ?? "", {
    skip: !activeCall?.contact.id,
  });
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const { data: voicemailDrop } = useGetVoicemailDropStatusQuery(
    { campaignId: campaign?.id ?? "", campaignContactId: activeCall?.id ?? "" },
    { skip: !campaign?.id || !activeCall?.id },
  );
  const { data: dispositions = [] } = useGetCallDispositionsQuery({});
  const { user } = useAuth();
  const [isSavingOutcome, setIsSavingOutcome] = useState(false);
  const firstDeal = contactDeals?.items[0];
  useKeyboardShortcuts(
    {
      ...dispositionShortcuts(dispositions, (dispositionName) => {
        if (activeCall) {
          selectDisposition({ id: activeCall.id }, dispositionName);
        }
      }),
      Enter: () => void handleSaveAndNext(),
      d: () => {
        if (activeCall && voicemailDrop?.available && !isCallActionPending) {
          dropVoicemail({ id: activeCall.id });
        }
      },
      h: () => void handleHangUp(),
    },
    Boolean(activeCall) && !areShortcutsPaused,
  );

  const handleHangUp = async () => {
    if (!activeCall) {
      return;
    }
    await endCall({ id: activeCall.id });
  };

  const handleHoldToggle = () => {
    if (!activeCall) {
      return;
    }
    if (activeCall.status === "ON_HOLD") {
      resumeCall({ id: activeCall.id });
      return;
    }
    holdCall({ id: activeCall.id });
  };

  const handleSaveAndNext = async () => {
    if (!activeCall) {
      return;
    }
    setIsSavingOutcome(true);
    try {
      await saveAndNext({ id: activeCall.id });
    } finally {
      setIsSavingOutcome(false);
    }
  };

  const handleDoNotCall = async () => {
    if (!activeCall || !campaign) {
      return;
    }
    try {
      await markDoNotCall({ campaign: { id: campaign.id }, campaignContact: { id: activeCall.id } }).unwrap();
      toast.success("Added to the Do Not Call list");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not add this number to the Do Not Call list"));
    }
  };

  const handleWrongNumber = async () => {
    if (!activeCall || !campaign) {
      return;
    }
    try {
      await markWrongNumber({ campaign: { id: campaign.id }, campaignContact: { id: activeCall.id } }).unwrap();
      toast.success("Marked as a wrong number");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not mark this number"));
    }
  };

  const describeNotesSaveState = (state: NotesSaveState | undefined) => {
    if (state === NotesSaveState.SAVING) {
      return "Saving notes";
    }
    if (state === NotesSaveState.SAVED) {
      return "Notes saved";
    }
    if (state === NotesSaveState.FAILED) {
      return "Notes didn't save. Keep typing to try again.";
    }
    return null;
  };

  if (!activeCall) {
    return null;
  }

  const contact = activeCall.contact;
  const isHeld = activeCall.status === "ON_HOLD";
  const isVoicemail = Boolean(
    activeCall.amdResult && (activeCall.amdResult.startsWith("machine") || activeCall.amdResult === "fax"),
  );
  const connectTime = connectSeconds(activeCall.answeredAt, activeCall.connectedAt);
  const notesSaveState = notesSaveStates[activeCall.id];
  const notesStatus = describeNotesSaveState(notesSaveState);
  const displayName = getContactDisplayName(contact) ?? activeCall.phoneNumber ?? "Unknown";
  const initials = getInitials(contact.firstName, contact.lastName);
  const otherPhones = (contact.phones ?? []).filter(
    (phone) => phone.phoneNumberE164 !== activeCall.phoneNumber && phone.phoneNumber !== activeCall.phoneNumber,
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 p-7">
      <LineSlotStrip />

      <div className="flex items-center gap-3">
        <Avatar className="h-12 w-12">
          {contact.photo && <AvatarImage src={contact.photo} alt={displayName} />}
          <AvatarFallback className="bg-primary/10 text-base font-semibold text-primary">
            {initials !== "?" ? initials : <User className="h-4 w-4" />}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="truncate text-2xl font-bold dark:text-white">{displayName}</div>
          <div className="truncate text-base text-muted-foreground">
            {contact.company}
            {contact.company && contact.id ? " · " : ""}
            {contact.id && (
              <button className="underline hover:text-foreground" onClick={() => navigate(`/contacts/${contact.id}/detail`)}>
                Open contact
              </button>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {isVoicemail && (
            <span className="status-pill status-pill-warning shrink-0 gap-1.5 px-3 py-1 text-sm">
              <Voicemail className="h-4 w-4" />
              Voicemail
            </span>
          )}
          <span
            className={cn(
              "status-pill shrink-0 gap-2 px-3 py-1 text-sm tabular-nums",
              isHeld ? "status-pill-warning" : "status-pill-success",
            )}
          >
            <span
              className="h-2.5 w-2.5 rounded-full animate-pulse"
              style={{ backgroundColor: isHeld ? "var(--warning)" : "var(--success)" }}
            />
            {isHeld ? `On hold · ${formatTime(activeCall.duration)}` : formatTime(activeCall.duration)}
          </span>
          <RecordingPill callId={activeCall.call?.id ?? null} />
          <CallControls
            twilioCall={roomCall}
            onKeypadPressed={onKeypadPressed}
            onHangPressed={handleHangUp}
            hasMuteShortcut={!areShortcutsPaused}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-5 text-base text-muted-foreground">
        <span>
          <b className="font-bold text-foreground">{activeCall.phoneNumber}</b> · live
        </span>
        {connectTime && <span>Connected {connectTime} s after the answer</span>}
        {otherPhones.map((phone) => {
          const skipLabel = phoneSkipLabel(phone);
          return (
            <span key={phone.id ?? phone.phoneNumberE164}>
              {phone.phoneNumber ?? phone.phoneNumberE164} · {skipLabel ? `skipped, ${skipLabel.toLowerCase()}` : "other number"}
            </span>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleHoldToggle} disabled={isCallActionPending}>
          {isHeld ? <Play className="mr-2 h-4 w-4" /> : <Pause className="mr-2 h-4 w-4" />}
          {isHeld ? "Resume" : "Hold"}
        </Button>
        {voicemailDrop?.messageName && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => dropVoicemail({ id: activeCall.id })}
            disabled={isCallActionPending || !voicemailDrop.available}
            title={voicemailDrop.reason ?? `Plays ${voicemailDrop.messageName} and hangs up`}
          >
            <Voicemail className="mr-2 h-4 w-4" />
            Drop voicemail
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={handleDoNotCall} disabled={isMarkingDoNotCall}>
          <PhoneOff className="mr-2 h-4 w-4" />
          Do not call
        </Button>
        <Button variant="outline" size="sm" onClick={handleWrongNumber} disabled={isMarkingWrongNumber}>
          <UserX className="mr-2 h-4 w-4" />
          Wrong number
        </Button>
        <Button variant="outline" size="sm" onClick={() => setIsHistoryOpen(true)} disabled={!fullContact}>
          <MessageSquare className="mr-2 h-4 w-4" />
          History and texts
        </Button>
        {voicemailDrop?.messageName && !voicemailDrop.available && voicemailDrop.reason && (
          <span className="text-sm text-muted-foreground">Voicemail drop unavailable: {voicemailDrop.reason}</span>
        )}
      </div>

      {contactDeals && contactDeals.items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {contactDeals.items.map((deal) => (
            <button
              key={deal.id}
              className="flex items-center gap-2 rounded-lg border border-border px-3.5 py-2 text-left transition-colors hover:bg-muted/50"
              onClick={() => navigate(`/deals/${deal.id}`)}
            >
              <Handshake className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="max-w-[240px] truncate text-base">{deal.propertyAddress || "Untitled deal"}</span>
              <Badge
                className="shrink-0 text-sm text-white"
                style={{ backgroundColor: deal.stage?.color ?? "#6B7280" }}
              >
                {deal.stage?.name}
              </Badge>
            </button>
          ))}
        </div>
      )}

      <LeadPropertyCards properties={fullContact?.properties ?? []} />

      <CallScriptPane
        scriptId={settings?.defaultScriptId}
        context={{
          firstName: contact.firstName,
          lastName: contact.lastName,
          company: contact.company,
          propertyAddress: firstDeal?.propertyAddress || fullContact?.properties?.[0]?.address,
          dealStage: firstDeal?.stage?.name,
          agentName: [user?.firstName, user?.lastName].filter(Boolean).join(" "),
          businessName: user?.activeOrganisation?.name,
        }}
      />

      <Textarea
        value={activeCall.notes ?? ""}
        onChange={(event) => setNotes({ id: activeCall.id }, event.target.value)}
        placeholder="Type notes during the call"
        className="min-h-0 flex-1 resize-none text-base"
      />
      {notesStatus && (
        <p
          className={cn(
            "-mt-3 text-sm",
            notesSaveState === NotesSaveState.FAILED ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {notesStatus}
        </p>
      )}

      <DispositionDock onSaveAndNext={handleSaveAndNext} isSaving={isSavingOutcome} />

      <LeadHistorySheet contact={fullContact} open={isHistoryOpen} onOpenChange={setIsHistoryOpen} />
    </div>
  );
}
